import { useEffect, useRef } from "react";

/**
 * CursorTrail — the mouse itself glows gold, with a soft golden veil
 * trailing behind. Leader sits exactly at the cursor (no delay),
 * followers lerp toward the previous point for a natural, gentle trail.
 * Auto-disables on touch devices.
 */
export const CursorTrail = () => {
  const dotsRef = useRef([]);

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      !window.matchMedia("(pointer: fine)").matches
    ) {
      return;
    }

    const NUM = 10;
    const dots = Array.from({ length: NUM }, () => ({ x: -200, y: -200 }));
    const els = dotsRef.current;

    let mouseX = -200;
    let mouseY = -200;
    let raf = 0;
    let running = true;

    const onMove = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
    };
    const onLeave = () => {
      mouseX = -400;
      mouseY = -400;
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseleave", onLeave);

    const tick = () => {
      if (!running) return;
      // Leader = exact cursor position (no lerp) → the "glowing mouse"
      dots[0].x = mouseX;
      dots[0].y = mouseY;
      // Trail: each follower gently chases the previous point
      for (let i = 1; i < NUM; i++) {
        // Smaller ease = longer, softer tail
        const ease = 0.28 - i * 0.015;
        dots[i].x += (dots[i - 1].x - dots[i].x) * ease;
        dots[i].y += (dots[i - 1].y - dots[i].y) * ease;
      }
      for (let i = 0; i < NUM; i++) {
        const el = els[i];
        if (el) {
          el.style.transform = `translate3d(${dots[i].x}px, ${dots[i].y}px, 0) translate(-50%, -50%)`;
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-[5]"
      data-testid="cursor-trail"
      style={{ mixBlendMode: "screen" }}
    >
      {Array.from({ length: 10 }).map((_, i) => {
        // Leader (i=0): a soft, gauzy halo around the cursor — no hard core
        // Followers: fading tail wisps
        const isLeader = i === 0;
        const size = isLeader ? 44 : Math.max(6, 22 - i * 2);
        const opacity = isLeader ? 0.45 : Math.max(0.03, 0.25 - i * 0.03);
        return (
          <span
            key={i}
            ref={(el) => (dotsRef.current[i] = el)}
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              width: `${size}px`,
              height: `${size}px`,
              borderRadius: "50%",
              background: isLeader
                ? "radial-gradient(circle, rgba(253,224,71,0.55) 0%, rgba(212,175,55,0.22) 40%, transparent 75%)"
                : "radial-gradient(circle, rgba(212,175,55,0.4), transparent 75%)",
              opacity,
              filter: isLeader ? "blur(6px)" : "blur(3px)",
              willChange: "transform",
            }}
          />
        );
      })}
    </div>
  );
};
